import GlobalContext from "@context/GlobalContext";
import useLoading from "@hooks/useLoading";
import { Check, Sparkles } from "lucide-react";
import { useContext, useTransition } from "react";

interface PlanFeature {
  text: string;
  highlighted?: boolean;
}

interface PlanCardProps {
  name: string;
  price: string;
  period: string;
  description: string;
  features: PlanFeature[];
  isPopular?: boolean;
  ctaText: string;
  onSelect?: () => void;
  setShowModal?: () => void;
}

export const PlanCard = ({
  name,
  price,
  period,
  description,
  features,
  isPopular,
  ctaText,
  onSelect,
  setShowModal,
}: PlanCardProps) => {
  const [state, dispatch] = useContext(GlobalContext);

  return (
    <div
      id="plans"
      className={`relative flex flex-col p-6 rounded-2xl transition-all duration-300 hover-lift sm:scroll-mt-30 scroll-mt-80 ${
        isPopular
          ? "bg-gradient-to-b from-primary-tail/10 to-card border-2 border-primary-tail/50 shadow-glow"
          : "bg-card border-1 border-border-tail hover:border-border/80"
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-tail text-primary-tail-foreground text-xs font-semibold rounded-full">
            <Sparkles className="w-3.5 h-3.5" />
            Best Value
          </span>
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">{name}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span
            className={`text-4xl font-bold ${
              isPopular ? "text-gradient-orange" : "text-foreground"
            }`}
          >
            {price}
          </span>
          <span className="text-muted-foreground text-sm">/{period}</span>
        </div>
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <div
              className={`mt-0.5 rounded-full p-0.5 ${
                isPopular ? "text-primary-tail" : "text-muted-foreground"
              }`}
            >
              <Check className="w-4 h-4" />
            </div>
            <span
              className={`text-sm ${
                feature.highlighted
                  ? "text-foreground font-medium"
                  : "text-muted-foreground"
              }`}
            >
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      {ctaText !== "Começar Grátis" && (
        <button
          onClick={() => {
            if (Object.keys(state.dadosusuario).length === 0) {
              setShowModal();
              return;
            }
            if (window.gtag) {
              window.gtag("event", "conversion", {
                send_to: "AW-17893671730/D6dbCPTU8OkbELKGr9RC",
              });
            }
            onSelect();
          }}
          className={`w-full py-3 px-4 rounded-xl font-medium text-sm transition-all duration-200 ${
            isPopular
              ? "bg-primary-tail text-primary-tail-foreground hover:bg-primary-tail/90 shadow-glow hover:shadow-glow-lg"
              : "bg-secondary-tail text-foreground hover:bg-secondary-tail/80 border-1 border-border-tail"
          }`}
        >
          {ctaText}
        </button>
      )}
    </div>
  );
};
